import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type { CadsSymbolId } from '../../bits/cads-icon-view-bit/cads-symbol-ids';
import type { CadsToastVariant } from '../../toast/cads-toast.config';

import type { CadsToastComponentConfig } from './cads-toast.config';

const VARIANT_ICON: Record<CadsToastVariant, CadsSymbolId> = {
  success: 'circle_check_filled',
  error: 'red-alert',
  info: 'information',
};

const VARIANT_COLOR: Record<CadsToastVariant, string> = {
  success: 'var(--cads-color-green)',
  error: 'var(--cads-color-red)',
  info: 'var(--cads-color-blue)',
};

/**
 * Ephemeral toast banner: leading semantic icon and message on a pastel surface. Rendered by
 * {@link CadsGlobalToastHostComponent}; callers use {@link CadsGlobalToastService} instead of mounting this directly.
 */
@Component({
  selector: 'cads-toast',
  imports: [CadsIconViewBitComponent, NgStyle],
  templateUrl: './cads-toast.component.html',
  styleUrl: './cads-toast.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CadsToastComponent {
  readonly config = input.required<CadsToastComponentConfig>();

  readonly variant = computed(() => this.config().variant ?? 'info');

  readonly isExiting = computed(() => this.config().exiting ?? false);

  readonly iconConfig = computed(() => ({
    icon: VARIANT_ICON[this.variant()],
    color: VARIANT_COLOR[this.variant()],
    size: 'small' as const,
    shape: 'icon' as const,
    collapsed: true,
    accessibleLabel: '',
  }));

  readonly accentCssVars = computed<Record<string, string>>(() => {
    const key = this.variant() === 'success' ? 'green' : this.variant() === 'error' ? 'red' : 'blue';
    return {
      '--cads-toast-base': `var(--cads-color-${key})`,
      '--cads-toast-deep': `var(--cads-color-${key}-deep)`,
      '--cads-toast-pastel': `var(--cads-color-${key}-pastel)`,
    };
  });

  readonly liveRole = computed(() => (this.variant() === 'error' ? 'alert' : 'status'));

  readonly livePoliteness = computed(() => (this.variant() === 'error' ? 'assertive' : 'polite'));
}
